import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, History } from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const shift = (m, n) => { const d = new Date(Number(m.slice(0, 4)), Number(m.slice(5, 7)) - 1 + n, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const fGiorno = (s) => new Date(`${s}T12:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
const fOra = (ts) => { const d = new Date(ts); return isNaN(d) ? "" : d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); };
const val = (x) => (x === null || x === undefined || x === "" ? "— (tolto)" : String(x));

const fetchCorr = async (categoria, structureId, mese) => (await api.get("/correzioni", { params: { categoria, mese, structure_id: structureId || undefined } })).data;

export function useCorrezioni(categoria, structureId, mese) {
  const { data = [] } = useQuery({ queryKey: ["correzioni", categoria, structureId, mese], enabled: !!mese, queryFn: () => fetchCorr(categoria, structureId, mese) });
  return useMemo(() => {
    const m = {};
    data.forEach((c) => { (m[`${c.ospite_id}_${c.data}`] = m[`${c.ospite_id}_${c.data}`] || []).unshift(c); });
    return m;
  }, [data]);
}

function Voce({ c }) {
  return (
    <div className="rounded-lg border bg-white p-2.5 text-xs" data-testid={`corr-voce-${c.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap"><b className="text-sm">{c.ospite_nome || "Ospite"}</b><span className="text-muted-foreground">ore {fOra(c.ts)}</span></div>
      <div className="mt-1 space-y-0.5">{Object.keys(c.dopo || {}).map((f) => (
        <div key={f}><span className="font-semibold">{f}:</span> <span className="line-through text-red-600">{val(c.prima?.[f])}</span> → <span className="font-semibold text-emerald-700">{val(c.dopo[f])}</span></div>
      ))}</div>
      <div className="mt-1 text-muted-foreground">Corretto da <b className="text-foreground">{c.operatore || "—"}</b>{c.operatore_prima ? <> · registrato prima da <b className="text-foreground">{c.operatore_prima}</b></> : null}</div>
    </div>
  );
}

export function ElencoCorrezioniBtn({ categoria, structureId, mese }) {
  const [open, setOpen] = useState(false);
  const [m, setM] = useState(mese);
  const { data = [], isLoading } = useQuery({ queryKey: ["correzioni", categoria, structureId, m], enabled: open, queryFn: () => fetchCorr(categoria, structureId, m) });
  const giorni = useMemo(() => { const g = {}; data.forEach((c) => { (g[c.data] = g[c.data] || []).push(c); }); return Object.entries(g).sort((a, b) => b[0].localeCompare(a[0])); }, [data]);
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => { setM(mese); setOpen(true); }} data-testid={`elenco-correzioni-btn-${categoria}`}><History size={15} className="mr-1" /> Elenco correzioni</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`elenco-correzioni-${categoria}`}>
          <DialogHeader><DialogTitle>Elenco correzioni</DialogTitle></DialogHeader>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setM(shift(m, -1))} data-testid="corr-mese-prev"><ChevronLeft size={15} /></Button>
            <span className="font-semibold text-sm min-w-[8rem] text-center" data-testid="corr-mese-label">{MESI[Number(m.slice(5, 7)) - 1]} {m.slice(0, 4)}</span>
            <Button size="sm" variant="outline" onClick={() => setM(shift(m, 1))} data-testid="corr-mese-next"><ChevronRight size={15} /></Button>
            <span className="ml-auto text-xs text-muted-foreground" data-testid="corr-totale">{data.length} {data.length === 1 ? "correzione" : "correzioni"}</span>
          </div>
          {isLoading ? <p className="text-sm text-muted-foreground">Caricamento…</p>
            : giorni.length === 0 ? <p className="text-sm text-muted-foreground py-4" data-testid="corr-vuoto">Nessuna correzione in questo mese.</p>
              : <div className="space-y-3">{giorni.map(([g, l]) => (
                <div key={g}>
                  <div className="text-[11px] font-bold uppercase tracking-wide text-orange-700 mb-1 capitalize">{fGiorno(g)}</div>
                  <div className="space-y-1.5">{l.map((c) => <Voce key={c.id} c={c} />)}</div>
                </div>))}</div>}
        </DialogContent>
      </Dialog>
    </>
  );
}
